import { useState, useMemo, useCallback } from 'react'
import {
  usePaymentAssignmentBenefitEventsQuery,
  usePaymentAssignmentDetailQuery,
} from '../../../api/finance-api-client'
import { PaymentCase } from '../Utils/types'
import {
  mapDetailToPaymentCase,
  mapBenefitEventsToActivityLog,
} from '../Utils/paymentAssignmentMappers'
import { parsePaymentAssignmentDetailCaseIdFromUrl } from '../Utils/paymentAssignmentUrlState'

export function usePaymentAssignmentDetail() {
  // Restore an open detail modal from the URL so a shared link reopens the same case.
  const [detailCaseId, setDetailCaseId] = useState<number | null>(() =>
    parsePaymentAssignmentDetailCaseIdFromUrl()
  )

  const { data: detailData, isLoading: detailLoading } =
    usePaymentAssignmentDetailQuery(detailCaseId)
  const { data: benefitEventsData, isLoading: benefitEventsLoading } =
    usePaymentAssignmentBenefitEventsQuery(detailCaseId)

  const detailCase: (PaymentCase & Record<string, unknown>) | null =
    useMemo(() => {
      if (detailCaseId === null || !detailData) return null
      const mappedDetailCase = mapDetailToPaymentCase(detailData, detailCaseId)
      const activityLogs = mapBenefitEventsToActivityLog(
        benefitEventsData ?? []
      )
      return { ...mappedDetailCase, activityLogs }
    }, [benefitEventsData, detailCaseId, detailData])

  const openDetailModal = useCallback((id: number) => {
    setDetailCaseId(id)
  }, [])

  const closeDetailModal = useCallback(() => {
    setDetailCaseId(null)
  }, [])

  return {
    detailCaseId,
    detailCase,
    detailLoading: detailLoading || benefitEventsLoading,
    openDetailModal,
    closeDetailModal,
  }
}
